import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Image, BookOpen, FileText, Users, ListMusic, Clock, Download,
  Headphones, Upload, Shield, BarChart3, TrendingUp, ScrollText,
  List, Sparkles, ChevronDown, ChevronRight, ChevronLeft,
  MoreHorizontal, Star, Tag, Search, Pin, X, HardDrive, Settings, PanelLeftClose, PanelLeftOpen, GitBranch, Mail, Phone, Crop, UserPlus, Loader2, Check, Scissors, Info, FolderTree, Bookmark, DollarSign, ShieldCheck,
} from 'lucide-react';
import { Button } from './ui/button';
import { useSearch } from '../contexts/SearchContext';
import { SHOW_CC_PAGES } from '../config/flags';
import TorahIcon from './icons/TorahIcon';
import UsaMapIcon from './icons/UsaMapIcon';
import useIsAdmin from '../hooks/useIsAdmin';
import SpeakerNameAutocomplete from './SpeakerNameAutocomplete';
// DonateButton import removed — Donate now lives in MORE_ITEMS as a menu link
import MobileBottomNav from './MobileBottomNav';
import {
  listPinnedSpeakers, unpinSpeaker, pinnedSpeakersChangeEvent,
} from '../utils/pinnedSpeakers';
import { getPortraitStyle } from '../utils/speakerPortraitOverrides';
import { getSpeakerFraming, PORTRAIT_ZOOM_CHANGE_EVENT } from '../utils/speakerZoomOverrides';
import { SHOW_AUTH } from '../config/flags';
import '../styles/premium-effects.css';
import AccessPill from './access/AccessPill';

const API = process.env.REACT_APP_BACKEND_URL;

// Top pinned items — always visible
const PRIMARY_ITEMS = [
  { id: 'home', icon: Home, label: 'Home', path: '/' },
  { id: 'cc-picture', icon: Image, label: 'CC Picture Library', path: '/cc-picture' },
  { id: 'transcripts', icon: ScrollText, label: 'Transcriptions', path: '/transcripts' },
];

// Rendered after By Topic / By Parsha so those two live above History + Downloads + Favorites
const AFTER_TOPICS_ITEMS = [
  { id: 'history', icon: Clock, label: 'History', path: '/history' },
  { id: 'downloads', icon: Download, label: 'My Downloads', path: '/download-center' },
  { id: 'favorites', icon: Star, label: 'My Favorites', path: '/favorites' },
];

// Rest of features (collapsed under "More features")
// Feb-2026 (user "on horizontal mode not all sidebars are there make
// all in right order"): reordered to mirror the mobile More page order
// exactly — Favorites/Downloads/History live in AFTER_TOPICS_ITEMS
// above so this list picks up from CC Family Tree onward and follows
// the user's dictated sequence: family tree → network → drive → more
// speakers → upload → settings → watchlist → donate → privacy → terms.
// Admin panel + admin-only tools sit at the bottom.
const MORE_ITEMS = [
  { id: 'cc-family-tree', icon: GitBranch, label: 'CC Family Tree', path: '/cc-family-tree' },
  { id: 'cc-network', icon: UsaMapIcon, label: 'CC Network', path: '/cc-network' },
  { id: 'drive', icon: HardDrive, label: 'Google Drive Link', external: 'https://drive.google.com/drive/folders/19Te68H4LntLbZ6YHtclC71_I72rUaZH4' },
  { id: 'speakers', icon: Users, label: 'More Speakers', path: '/speakers' },
  { id: 'upload-shiur', icon: Upload, label: 'Upload My Recordings', path: '/upload' },
  { id: 'settings', icon: Settings, label: 'Settings', path: '/settings' },
  { id: 'watchlist', icon: Bookmark, label: 'Watchlist', path: '/watchlist' },
  { id: 'donate', icon: DollarSign, label: 'Donate', path: '/donate' },
  { id: 'privacy', icon: ShieldCheck, label: 'Privacy Policy', path: '/privacy' },
  { id: 'terms', icon: FileText, label: 'Terms of Service', path: '/terms' },
  // Extras kept accessible for power users (not in the More page).
  { id: 'trending', icon: TrendingUp, label: 'Trending', path: '/trending' },
  { id: 'playlists', icon: List, label: 'Playlists', path: '/playlists' },
  { id: 'audio-converter', icon: Headphones, label: 'File Converter', path: '/audio-converter' },
  { id: 'library-export', icon: HardDrive, label: 'Download Library', path: '/library-export' },
  // Feb 2026 (user "make about the 2nd lowest of tabs on desktop as i
  // dont want it to be focused on as not ready yet"): About sits
  // ONE ABOVE the admin block so it's the second-to-last public item
  // — visible but not attention-grabbing.
  { id: 'about', icon: Info, label: 'About', path: '/about' },
  // Admin (visible in sidebar; the /admin route itself is passcode-gated
  // from the More page and always require_admin on the backend).
  { id: 'admin', icon: Shield, label: 'Admin Panel', path: '/admin' },
  { id: 'portrait-crop', icon: Crop, label: 'Portrait Crop Studio', path: '/admin/portrait-crop' },
  { id: 'folder-manager', icon: FolderTree, label: 'Folder Manager', path: '/admin/folders' },
];

const getButtonClass = (isActive, isHighlight) => {
  const baseClass =
    'w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-all duration-300 ease-out cursor-pointer border-l-[3px]';
  if (isHighlight) {
    return `${baseClass} border-transparent rounded-md bg-[hsl(44,62%,37%)] text-white font-semibold hover:bg-[hsl(43,67%,29%)] active:scale-95 transition-transform`;
  }
  if (isActive) {
    // Feb 2026 (user: "make the clicked on sidebar text look a nicer
    // gold similar to rest of website not the brown or dark gold it
    // currently is"). Bright warm gold `#f6dd8f` matches the mini
    // player CTAs and radial hover pucks — reads clearly on the
    // translucent gold pill background sitting over the teal sidebar.
    return `${baseClass} border-[hsl(44,62%,37%)] text-[#f6dd8f] font-bold rounded-r-md vaad-nav-pill--active !border-l-[3px] !border-l-[hsl(44,62%,37%)]`;
  }
  return `${baseClass} border-transparent rounded-md text-[#cfd7ea] hover:bg-white/10 hover:text-white font-semibold`;
};

const MenuItem = ({ item, isActive, onClick, collapsed }) => (
  <button
    className={getButtonClass(isActive, item.highlight)}
    onClick={onClick}
    data-testid={`sidebar-item-${item.id}`}
    title={collapsed ? item.label : undefined}
  >
    <item.icon className="w-4 h-4 shrink-0" />
    {!collapsed && <span className="flex-1 text-left truncate">{item.label}</span>}
  </button>
);

/**
 * Admin-only drag-and-drop zone that installs a new speaker portrait via
 * POST /api/portrait/upload. Handles the entire pipeline (rembg, cache
 * sizes, DB upsert) server-side — the user drops one image, we do the rest.
 *
 * `mode` prop: 'default' (standard padded rembg) or 'hat_neck' (tuned for
 * tough hat + neck cases the default cutout mangles).
 */
const SpeakerPortraitDropZone = ({ mode = 'default', title, subtitle, testidPrefix = 'sidebar-portrait' }) => {
  const [name, setName] = React.useState('');
  const [dragging, setDragging] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState('');
  const inputRef = React.useRef(null);

  const submit = async (file) => {
    setErr('');
    setResult(null);
    if (!name.trim()) { setErr('Enter the speaker name first.'); return; }
    if (!file) { setErr('Drop or pick an image.'); return; }
    if (!file.type.startsWith('image/')) { setErr('Only image files are accepted.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('speaker', name.trim());
      fd.append('mode', mode);
      const r = await fetch(`${API}/api/portrait/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!r.ok) throw new Error((await r.text()) || `HTTP ${r.status}`);
      const data = await r.json();
      setResult(data);
      setTimeout(() => window.dispatchEvent(new Event('vaad-speaker-refresh')), 400);
    } catch (e) {
      setErr(e.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const accent = mode === 'hat_neck' ? '#f4a261' : '#e5c976';
  return (
    <div
      className={`mt-2 rounded-lg border ${dragging ? 'bg-white/10' : 'bg-white/[0.02]'} p-3 transition-colors`}
      style={{ borderColor: dragging ? accent : 'rgba(255,255,255,0.10)' }}
      data-testid={`${testidPrefix}-uploader`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) submit(f);
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        {mode === 'hat_neck'
          ? <Scissors className="w-3.5 h-3.5" style={{ color: accent }} />
          : <UserPlus className="w-3.5 h-3.5" style={{ color: accent }} />}
        <p className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: accent }}>
          {title || 'Upload Speaker Portrait'}
        </p>
      </div>
      {subtitle && (
        <p className="text-[9px] text-white/50 mb-2 leading-snug">{subtitle}</p>
      )}
      <SpeakerNameAutocomplete
        value={name}
        onChange={setName}
        onPick={setName}
        placeholder="Speaker name (start typing…)"
        disabled={busy}
        testid={`${testidPrefix}-speaker-name`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className={`w-full text-[11px] font-semibold py-1.5 rounded-md border transition-colors mt-2 ${
          busy ? 'bg-white/5 border-white/10 text-white/50' : 'hover:bg-white/10'
        }`}
        style={{
          background: busy ? undefined : `${accent}1a`,
          borderColor: busy ? undefined : `${accent}66`,
          color: busy ? undefined : accent,
        }}
        data-testid={`${testidPrefix}-pick-btn`}
      >
        {busy
          ? <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" />Uploading…</span>
          : 'Drop image or click to pick'}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => submit(e.target.files?.[0])}
        data-testid={`${testidPrefix}-file-input`}
      />
      {result && (
        <p className="mt-2 text-[10px] text-emerald-300 inline-flex items-center gap-1" data-testid={`${testidPrefix}-success`}>
          <Check className="w-3 h-3" /> Installed for <span className="font-semibold truncate">{result.speaker}</span>
        </p>
      )}
      {err && (
        <p className="mt-2 text-[10px] text-red-300" data-testid={`${testidPrefix}-error`}>{err}</p>
      )}
    </div>
  );
};

/** Pinned-speaker avatar that mirrors the home-grid framing:
 *  applies the same per-speaker portrait style + zoom overrides so the
 *  pinned pic is cropped/zoomed exactly like the icon on the home page.
 */
const PinnedSpeakerAvatar = ({ name, image, initials }) => {
  const portrait = getPortraitStyle(name);
  const [framing, setFraming] = React.useState(() => getSpeakerFraming(name));
  React.useEffect(() => {
    const sync = () => setFraming(getSpeakerFraming(name));
    sync();
    window.addEventListener(PORTRAIT_ZOOM_CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(PORTRAIT_ZOOM_CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [name]);
  const effectiveScale = framing?.scale ?? portrait.scale ?? 1;
  const effectiveRotate = framing?.rotate ?? 0;
  const effectivePosition = framing
    ? `${framing.posX}% ${framing.posY}%`
    : portrait.position;
  if (!image) {
    return (
      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[hsl(44,62%,37%)] to-[hsl(43,67%,25%)] flex items-center justify-center text-[9px] font-bold text-white shrink-0">
        {initials}
      </div>
    );
  }
  return (
    <div className="w-6 h-6 rounded-full overflow-hidden ring-1 ring-white/15 shrink-0">
      <img
        src={image}
        alt=""
        className="w-full h-full"
        style={{
          objectFit: portrait.fit,
          objectPosition: effectivePosition,
          transform: `scale(${effectiveScale}) rotate(${effectiveRotate}deg)`,
          transformOrigin: effectivePosition,
        }}
        draggable={false}
      />
    </div>
  );
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openSearch } = useSearch();
  const isAdmin = useIsAdmin();
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);
  const [pinnedSpeakers, setPinnedSpeakers] = React.useState(() => listPinnedSpeakers());

  // Collapsed state — persisted; default true on mobile so the main content
  // gets the full iPhone screen width instead of sharing with a 224px rail.
  const [collapsedPref, setCollapsedPref] = React.useState(() => {
    try {
      const v = localStorage.getItem('vaad_sidebar_collapsed');
      if (v === 'true') return true;
      if (v === 'false') return false;
    } catch { /* ignore */ }
    if (typeof window !== 'undefined') return window.innerWidth < 768;
    return false;
  });
  React.useEffect(() => {
    try { localStorage.setItem('vaad_sidebar_collapsed', String(collapsedPref)); } catch { /* ignore */ }
  }, [collapsedPref]);

  // Feb 2026 (user "make the sidebar slim by default in Media Center"):
  // any route that renders <MediaLibraryTabs/> forces the sidebar into
  // slim mode so the tab-row + player content get the full width.
  // Toggling the sidebar open on those pages is still allowed — the
  // user's preference is only overridden while the pathname matches.
  const isMediaCenter = React.useMemo(() => {
    const p = location.pathname || '';
    return p.startsWith('/videos')
      || p.startsWith('/cc-picture')
      || p.startsWith('/cc-publications');
  }, [location.pathname]);
  // Feb-2026 (user "preview pane looks so weird"): viewports between the
  // mobile cutoff (640) and a comfortable desktop (1024) — tablets and
  // the platform's embedded preview iframe — can't fit the 224px rail
  // plus content. Force slim mode there regardless of the saved
  // preference; the user can still expand manually for the session.
  const [narrowViewport, setNarrowViewport] = React.useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 640 && window.innerWidth < 1024,
  );
  React.useEffect(() => {
    const onResize = () => setNarrowViewport(window.innerWidth >= 640 && window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const forceSlim = isMediaCenter || narrowViewport;
  const [mediaCenterOverride, setMediaCenterOverride] = React.useState(false);
  React.useEffect(() => {
    // Reset the "user opened the sidebar" flag when leaving the
    // force-slim contexts (media-center routes / narrow viewport).
    if (!forceSlim) setMediaCenterOverride(false);
  }, [forceSlim]);
  const collapsed = forceSlim && !mediaCenterOverride ? true : collapsedPref;
  const setCollapsed = React.useCallback((v) => {
    const next = typeof v === 'function' ? v(collapsed) : v;
    if (forceSlim) setMediaCenterOverride(!next);
    setCollapsedPref(next);
  }, [collapsed, forceSlim]);

  React.useEffect(() => {
    const sync = () => setPinnedSpeakers(listPinnedSpeakers());
    window.addEventListener(pinnedSpeakersChangeEvent, sync);
    return () => window.removeEventListener(pinnedSpeakersChangeEvent, sync);
  }, []);

  // parshiot & topics for the collapsible nested lists
  const [parshiot, setParshiot] = React.useState([]);
  const [topics, setTopics] = React.useState([]);
  const parshaOpenDefault = location.pathname.startsWith('/parsha');
  const topicOpenDefault = location.pathname.startsWith('/topic');
  const [parshaOpen, setParshaOpen] = React.useState(parshaOpenDefault);
  const [topicOpen, setTopicOpen] = React.useState(topicOpenDefault);

  React.useEffect(() => {
    // Fetch once on mount
    fetch(`${API}/api/parshiot`).then((r) => r.json()).then(setParshiot).catch(() => {});
    fetch(`${API}/api/topics`).then((r) => r.json()).then(setTopics).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (location.pathname.startsWith('/parsha')) setParshaOpen(true);
    if (location.pathname.startsWith('/topic')) setTopicOpen(true);
  }, [location.pathname]);

  const toggleTheme = () => { /* moved to Settings */ };

  const isMoreActive = React.useMemo(
    () => MORE_ITEMS.some((i) => location.pathname.startsWith(i.path) && i.path !== '/'),
    [location.pathname]
  );
  const [moreOpen, setMoreOpen] = React.useState(isMoreActive);
  React.useEffect(() => {
    if (isMoreActive) setMoreOpen(true);
  }, [isMoreActive]);

  React.useEffect(() => {
    if (!SHOW_AUTH) { setIsLoggedIn(true); return; }
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        setIsLoggedIn(response.ok);
      } catch {
        setIsLoggedIn(false);
      }
    };
    checkAuth();
  }, []);

  const handleNavigation = (path) => { if (path) navigate(path); };
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <MobileBottomNav />
      <div
        className={`hidden sm:flex ${collapsed ? 'w-14' : 'w-56'} h-screen text-[#cfd7ea] flex-col relative shrink-0 transition-[width] duration-200`}
        style={{
          // Feb-2026 (user: "make the side bar on desktop … be liquid
          // glass"): the whole side rail is now a translucent frosted
          // panel using the logo teal instead of a solid slab. Deep
          // enough opacity to keep text legible, but the page content
          // underneath still bleeds through subtly when scrolling.
          //
          // Feb-2026 (user: "I like the sidebar liquid glass but make
          // the letters look more sharp"): bumped panel opacity from
          // 0.82-0.90 → 0.92-0.97 so text has near-solid backing and
          // stops looking soft against the translucent blur. Font
          // smoothing tuned below.
          background: 'linear-gradient(180deg, rgba(3,39,58,0.94) 0%, rgba(5,51,74,0.92) 55%, rgba(3,39,58,0.97) 100%)',
          WebkitBackdropFilter: 'blur(22px) saturate(170%)',
          backdropFilter: 'blur(22px) saturate(170%)',
          borderRight: '1px solid rgba(230,200,100,0.22)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 4px 0 24px rgba(0,0,0,0.15)',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
        }}
        data-testid="app-sidebar"
        data-collapsed={collapsed}
      >
      {/* Logo — compact one-line */}
      <div
        className={`px-3 py-2.5 flex items-center gap-2.5 border-b border-white/10 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0 ${collapsed ? 'justify-center px-2' : ''}`}
        onClick={() => navigate('/')}
        data-testid="sidebar-logo"
      >
        <img
          src="/vaad-otzar-logo.jpg"
          alt="The Vaad Otzar"
          className="w-8 h-8 rounded-md object-cover ring-1 ring-white/15 shrink-0"
        />
        {!collapsed && (
          <div className="min-w-0 leading-tight">
            <h1 className="text-white font-semibold text-[13px] truncate">The Vaad Otzar</h1>
            <p className="text-[9px] uppercase tracking-[0.14em] text-[#a8b3c9]">Audio Library</p>
          </div>
        )}
      </div>

      {SHOW_AUTH && !isLoggedIn && !collapsed && (
        <div className="px-3 pb-1 pt-3 flex-shrink-0">
          <Button
            onClick={() => navigate('/login')}
            className="w-full rounded-full bg-[hsl(44,62%,37%)] hover:bg-[hsl(43,67%,29%)] text-white font-semibold shadow-md shadow-black/30 active:scale-95 transition-transform"
            size="sm"
            data-testid="sidebar-signin-btn"
          >
            Sign In
          </Button>
        </div>
      )}

      {/* Feb 2026 (user): removed the sidebar Search button — the
          Home page's prominent search bar covers the same job, and the
          user wants the section list to start flush under the logo. */}

      <div className="flex-1 overflow-y-auto px-2 pt-2 pb-4">
        {pinnedSpeakers.length > 0 && !collapsed && (
          <div className="mb-3">
            <div className="px-3 pt-2 pb-1.5 flex items-center gap-1.5">
              <Pin className="w-3 h-3 text-[hsl(44,62%,60%)]" />
              <span className="flex-1 text-[10px] uppercase tracking-[0.16em] text-[#8591ad]/70 font-semibold">
                Pinned Speakers
              </span>
            </div>
            {pinnedSpeakers.map((sp) => {
              const speakerRoute = `/speaker/${encodeURIComponent(sp.name)}`;
              const active = location.pathname === speakerRoute;
              const initials = (sp.name || '?')
                .replace(/^(Rabbi|R'|R\.|Rav|Ha'?Rav|Harav)\s+/i, '')
                .trim()
                .split(/\s+/)
                .slice(0, 2)
                .map((p) => p[0] || '')
                .join('')
                .toUpperCase();
              return (
                <div
                  key={sp.name}
                  className={`group flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${
                    active
                      ? 'bg-white/[0.08] text-white'
                      : 'text-[#8591ad] hover:bg-white/[0.05] hover:text-white'
                  }`}
                  onClick={() => navigate(speakerRoute)}
                  data-testid={`sidebar-pinned-${sp.name}`}
                >
                  {sp.image ? (
                    <PinnedSpeakerAvatar name={sp.name} image={sp.image} initials={initials} />
                  ) : (
                    <PinnedSpeakerAvatar name={sp.name} image={null} initials={initials} />
                  )}
                  <span className="flex-1 text-[13px] truncate">{sp.name}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); unpinSpeaker(sp.name); }}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-white/10 text-white/50 hover:text-white transition-opacity"
                    aria-label={`Unpin ${sp.name}`}
                    data-testid={`sidebar-pinned-remove-${sp.name}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Feb 2026 (user): removed the "Browse" header so the Home
            item sits flush right under the logo divider. */}
        {PRIMARY_ITEMS.map((item) => (
          <MenuItem
            key={item.id}
            item={item}
            isActive={item.external ? false : isActive(item.path)}
            collapsed={collapsed}
            onClick={() => {
              if (item.external && item.href) {
                window.open(item.href, '_blank', 'noopener,noreferrer');
              } else {
                handleNavigation(item.path);
              }
            }}
          />
        ))}

        {/* --- By Topic (single link — goes straight to /topics grid) --- */}
        {!collapsed && (
        <>
        <button
          type="button"
          onClick={() => navigate('/topics')}
          className={getButtonClass(isActive('/topic'), false)}
          data-testid="sidebar-topics-toggle"
        >
          <Tag className="w-4 h-4" />
          <span className="flex-1 text-left">By Topic</span>
        </button>

        {/* --- By the Parsha (single link — goes straight to /parsha grid) --- */}
        <button
          type="button"
          onClick={() => navigate('/parsha')}
          className={getButtonClass(isActive('/parsha'), false)}
          data-testid="sidebar-parsha-toggle"
        >
          <TorahIcon className="w-4 h-4" />
          <span className="flex-1 text-left">By the Parsha</span>
        </button>
        </>
        )}

        {/* History + Downloads live BELOW the By Topic / By Parsha sections */}
        {AFTER_TOPICS_ITEMS.map((item) => (
          <MenuItem
            key={item.id}
            item={item}
            isActive={isActive(item.path)}
            collapsed={collapsed}
            onClick={() => handleNavigation(item.path)}
          />
        ))}

        {!collapsed && (
        <>

        {/* More features collapsible */}
        <button
          type="button"
          onClick={() => setMoreOpen((o) => !o)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold text-[#a8b3c9] hover:bg-white/10 hover:text-white transition-colors duration-300 mt-2"
          data-testid="sidebar-and-more-toggle"
          aria-expanded={moreOpen}
        >
          <MoreHorizontal className="w-4 h-4" />
          <span className="flex-1 text-left">More features</span>
          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${moreOpen ? 'rotate-180' : ''}`} />
        </button>
        {moreOpen && !collapsed && (
          <div className="ml-2 mt-1 border-l border-white/10 pl-2" data-testid="sidebar-and-more-panel">
            {MORE_ITEMS.filter((item) => {
              // Feb-2026 (user): admin-only links must NEVER show for
              // logged-out visitors or regular users. Anyone browsing
              // thevaadotzar.com without an admin session gets a clean
              // sidebar that has no "Admin Panel" or "Portrait Crop
              // Studio" entries.
              if (item.id === 'admin' || item.id === 'portrait-crop' || item.id === 'folder-manager') return isAdmin;
              // June-2026 (user): CC Network + CC Family Tree removed (flag to restore).
              if (item.id === 'cc-network' || item.id === 'cc-family-tree') return SHOW_CC_PAGES;
              return true;
            }).map((item) => (
              <MenuItem
                key={item.id}
                item={item}
                isActive={item.external ? false : isActive(item.path)}
                onClick={() => {
                  if (item.external) {
                    window.open(item.external, '_blank', 'noopener,noreferrer');
                  } else {
                    handleNavigation(item.path);
                  }
                }}
              />
            ))}
            {isAdmin && (
              <>
                <SpeakerPortraitDropZone
                  mode="default"
                  title="Upload Speaker Portrait"
                  testidPrefix="sidebar-portrait"
                />
                <SpeakerPortraitDropZone
                  mode="hat_neck"
                  title="Recrop Hat / Neck Case"
                  subtitle="For tough hat + long-neck speakers the default cutout mangles. Uses wider padding + alpha matting for cleaner peyos & hat brims."
                  testidPrefix="sidebar-portrait-recrop"
                />
              </>
            )}
          </div>
        )}
        </>
        )}
      </div>

      <div className={`px-3 py-2 border-t border-white/10 flex-shrink-0 ${collapsed ? 'space-y-1' : 'space-y-1.5'}`}>
        {!collapsed && (
          <div className="flex justify-center pb-1" data-testid="sidebar-access-slot">
            <AccessPill variant="dark" />
          </div>
        )}
        {/* Pretty Vaad Otzar Drive link — gold-bordered pill */}
        {collapsed ? (
          <a
            href="https://drive.google.com/drive/folders/19Te68H4LntLbZ6YHtclC71_I72rUaZH4"
            target="_blank"
            rel="noopener noreferrer"
            className="w-9 h-9 mx-auto flex items-center justify-center rounded-lg bg-gradient-to-br from-[#c9a437] to-[#8c691b] hover:from-[#e5c976] hover:to-[#9a7523] shadow-md shadow-black/40 ring-1 ring-[#e5c976]/40 hover:ring-[#e5c976] transition-all"
            data-testid="sidebar-vaad-drive"
            aria-label="Open Vaad Otzar Google Drive Link"
            title="Google Drive Link"
          >
            <HardDrive className="w-4 h-4 text-white" />
          </a>
        ) : (
          <a
            href="https://drive.google.com/drive/folders/19Te68H4LntLbZ6YHtclC71_I72rUaZH4"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] font-medium liquid-glass text-[#cfd7ea] hover:text-white transition-all"
            style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(230,200,100,0.28)' }}
            data-testid="sidebar-vaad-drive"
            aria-label="Open Vaad Otzar Google Drive Link"
          >
            <div className="w-5 h-5 rounded-md bg-white/5 flex items-center justify-center shrink-0 ring-1 ring-white/10">
              <HardDrive className="w-3 h-3 text-[#e5c976]/80" />
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <p className="text-[9px] uppercase tracking-[0.14em] text-[#8591ad]">Vaad Otzar</p>
              <p className="text-[12px] truncate">Google Drive Link</p>
            </div>
            <ChevronRight className="w-3 h-3 text-[#8591ad] group-hover:text-white group-hover:translate-x-0.5 transition-transform" />
          </a>
        )}
        <button
          type="button"
          onClick={toggleTheme}
          className="hidden"
          data-testid="theme-toggle"
          aria-hidden="true"
        />
      </div>
      {/* Footer with contact info + collapse arrow anchored to the right */}
      {collapsed ? (
        <div className="px-2 py-2 border-t border-white/10 flex justify-center flex-shrink-0">
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            className="flex items-center justify-center rounded-full bg-gradient-to-b from-[#c9a437] to-[#8c691b] hover:from-[#e5c976] hover:to-[#9a7523] text-white p-1.5 shadow-md shadow-black/40 ring-1 ring-[#e5c976]/40 hover:ring-[#e5c976] transition-all"
            data-testid="sidebar-collapse-toggle"
            aria-label="Expand sidebar"
            title="Expand sidebar"
          >
            <PanelLeftOpen className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="px-4 py-3 border-t border-white/10 flex-shrink-0 flex items-start gap-2">
          <div className="flex-1 min-w-0 text-[10px] text-[#8591ad] tracking-wide leading-relaxed">
            <a
              // Feb 2026 (user: "pressing on my email at bottom will
              // open ones Gmail and have my name in sending too so
              // one doesn't need to manually copy it"). Gmail's web
              // compose URL fills both the "To" address and the
              // display name; opens in a new tab so people already
              // signed into gmail.com land in a ready-to-send
              // compose window instead of having to copy-paste.
              // Ctrl/cmd-click still gives them the raw mailto if
              // they prefer their native mail client.
              href="https://mail.google.com/mail/?view=cm&fs=1&to=Akiva%20Paris%20%3CAkivaParis%40gmail.com%3E"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 hover:text-white transition-colors truncate"
              data-testid="sidebar-contact-email"
              onContextMenu={(e) => {
                // Right-click keeps the classic mailto: — puts name
                // AND address in the To header so any desktop mail
                // client (Outlook, Apple Mail, Thunderbird) opens
                // with "Akiva Paris <AkivaParis@gmail.com>" filled
                // in.
                e.preventDefault();
                window.location.href = 'mailto:%22Akiva%20Paris%22%20%3CAkivaParis%40gmail.com%3E';
              }}
              title="Click to compose in Gmail (right-click for native mail app)"
            >
              <Mail className="w-3 h-3 text-[#e5c976] shrink-0" />
              <span className="truncate">AkivaParis@gmail.com</span>
            </a>
            <a
              href="sms:+13054979507"
              className="flex items-center gap-1.5 mt-1 hover:text-white transition-colors"
              data-testid="sidebar-contact-phone"
              title="Tap on a phone to start a text message"
            >
              <Phone className="w-3 h-3 text-[#e5c976] shrink-0" />
              <span>305-497-9507</span>
            </a>
          </div>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="mt-0.5 flex items-center justify-center rounded-full bg-gradient-to-b from-[#c9a437] to-[#8c691b] hover:from-[#e5c976] hover:to-[#9a7523] text-white p-1.5 shadow-md shadow-black/40 ring-1 ring-[#e5c976]/40 hover:ring-[#e5c976] transition-all shrink-0"
            data-testid="sidebar-collapse-toggle"
            aria-label="Hide sidebar"
            title="Hide sidebar"
          >
            <PanelLeftClose className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
    </>
  );
};

export default Sidebar;
